import { StyleSheet, Text, TextInput, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { IconButton, useDisclosure } from '../index';
import type { DraftSet } from './state/loggerDraft';
import type { useTheme } from './theme';
import { CardioDurationField } from './CardioDurationField';
import { useFieldFocusReporter } from './useFieldFocus';

// Cardio entry block for the timeline FocusPanel (slice 2.5) — hours:minutes:
// seconds duration + a disclosure for the optional run details (distance /
// weight / HR / calories). Extracted from FocusPanel to keep that file under
// the 500-line budget; the cardio vs strength split is a real seam.
//
// TODO(cleanup, post-luglio): the optional-field rows duplicate CardioSetRow's
// disclosure — lift to a shared CardioOptionalFields component when the card
// logger retires under the RNW convergence (next/web-mobile-convergence.md).

// The cardio optional fields surfaced under the duration entry (31/05 smoke
// item 4 — run details weren't enterable in timeline mode).
const CARDIO_OPTIONALS: ReadonlyArray<{
  key: 'distanceM' | 'weight' | 'avgHrBpm' | 'calories';
  labelKey: string;
  unit: string;
  maxLength: number;
}> = [
  { key: 'distanceM', labelKey: 'mobile.logger.cardioDistance', unit: 'm', maxLength: 6 },
  { key: 'weight', labelKey: 'mobile.logger.cardioWeight', unit: 'kg', maxLength: 6 },
  { key: 'avgHrBpm', labelKey: 'mobile.logger.cardioHr', unit: 'bpm', maxLength: 3 },
  { key: 'calories', labelKey: 'mobile.logger.cardioCalories', unit: 'kcal', maxLength: 5 },
];

export function FocusPanelCardio({
  set,
  onChange,
  theme,
}: {
  set: DraftSet;
  onChange: (
    patch: Partial<
      Pick<DraftSet, 'duration' | 'distanceM' | 'weight' | 'avgHrBpm' | 'calories'>
    >,
  ) => void;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  // A cardio set's cells hold the keyboard like any other (TM-18a). Reported
  // from one reporter with the id at call time, because these rows come out of
  // a map — and a branch that stays silent tells the resume "nobody is typing"
  // on exactly the sets it was never wired for.
  const fieldFocus = useFieldFocusReporter();
  // The optional run-detail fields (distance/weight/HR/cal) fold under the
  // duration entry; their `list` toggle rides the END of the h:min:sec row
  // (MX-5 slice 2 — it used to sit on the timer row, which no longer exists),
  // aligned to the input boxes. A hold has no optional details, so there it
  // simply isn't passed and the row stays as it was.
  const optionals = useDisclosure(false);

  return (
    <View style={styles.block}>
      <Text style={styles.label}>
        {t('mobile.timeline.durationLabel', { defaultValue: 'Duration' })}
      </Text>
      <CardioDurationField
        seconds={set.duration}
        onChange={(duration) => onChange({ duration })}
        trailing={
          <IconButton
            theme={theme}
            name="list"
            onPress={optionals.toggle}
            expanded={optionals.expanded}
            active={optionals.expanded}
            // The row's gap is 4, not 8: a symmetric slop would reach ~4pt
            // into the seconds input's own box and steal taps meant for its
            // right edge (a11y audit 2026-07-22). 8 on the three free sides,
            // 2 towards the cell at its left.
            hitSlop={{ top: 8, bottom: 8, left: 2, right: 8 }}
            accessibilityLabel={
              optionals.expanded
                ? t('mobile.logger.cardioOptionalsHide')
                : t('mobile.logger.cardioOptionalsShow')
            }
          />
        }
        theme={theme}
      />
      {optionals.expanded ? (
        <View style={styles.optionals}>
          {CARDIO_OPTIONALS.map(({ key, labelKey, unit, maxLength }) => (
            <View key={key} style={styles.optionalRow}>
              <Text style={styles.optionalLabel}>{t(labelKey)}</Text>
              <TextInput
                style={styles.optionalInput}
                keyboardType="decimal-pad"
                inputMode="decimal"
                placeholder="—"
                placeholderTextColor={theme.palette.ink3}
                value={set[key]}
                onChangeText={(v) =>
                  onChange({ [key]: v } as Partial<Pick<DraftSet, typeof key>>)
                }
                onFocus={() => fieldFocus.onFocus(`cardio.${key}`)}
                onBlur={() => fieldFocus.onBlur(`cardio.${key}`)}
                maxLength={maxLength}
              />
              <Text style={styles.optionalUnit}>{unit}</Text>
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    block: { flex: 1, gap: s.s2 },
    label: { color: p.ink3, fontSize: ty.sizes.xs, fontWeight: '600' },
    optionals: { gap: s.s2 },
    optionalRow: { flexDirection: 'row', alignItems: 'center', gap: s.s2 },
    optionalLabel: { flex: 1, color: p.ink2, fontSize: ty.sizes.sm },
    optionalInput: {
      width: 96,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r2,
      paddingHorizontal: s.s3,
      paddingVertical: s.s2,
      color: p.ink,
      fontSize: ty.sizes.base,
      textAlign: 'center',
      backgroundColor: p.bg,
    },
    optionalUnit: {
      width: 36,
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: '600',
    },
  });
}
